import { TrendingUp, Layers, Target } from "lucide-react";

type Driver = { slug: string; signals?: number; engagement?: number; latest_at?: string | null };
type Breakdown = {
  velocity?: Driver[];
  volume?: Driver[];
  relevance?: Driver[];
  by_source?: Driver[];
};

function timeAgo(iso?: string | null) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) return "<1h ago";
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Row({
  icon, label, value, drivers, render, fallback,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  drivers?: Driver[];
  render: (d: Driver) => string;
  fallback: string;
}) {
  const list = drivers && drivers.length > 0 ? drivers : null;
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="text-primary mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-mono text-[10px] uppercase text-muted-foreground">
            {label} · <span className="text-foreground">{value}</span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {list ? (
            <span>
              Driven by{" "}
              {list.map((d, i) => (
                <span key={d.slug}>
                  {i > 0 && ", "}
                  <span className="text-foreground font-medium">{d.slug}</span>
                  <span className="text-muted-foreground"> ({render(d)})</span>
                </span>
              ))}
            </span>
          ) : (
            <span className="italic">{fallback}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function ScoreExplanation({
  velocity, volume, relevance, sourceSlugs, breakdown,
}: {
  velocity: number;
  volume: number;
  relevance: number;
  sourceSlugs: string[];
  breakdown?: Breakdown | null;
}) {
  const fallbackList = sourceSlugs.slice(0, 3).join(", ") || "no sources recorded";
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/20 p-3 mt-4">
      <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
        why this score
      </div>
      <Row
        icon={<TrendingUp className="size-3.5" />}
        label="Velocity"
        value={velocity}
        drivers={breakdown?.velocity}
        render={(d) => `latest ${timeAgo(d.latest_at)}`}
        fallback={`Most recent activity from ${fallbackList}`}
      />
      <Row
        icon={<Layers className="size-3.5" />}
        label="Volume"
        value={volume}
        drivers={breakdown?.volume}
        render={(d) => `${d.signals ?? 0} signals`}
        fallback={`Signal density across ${fallbackList}`}
      />
      <Row
        icon={<Target className="size-3.5" />}
        label="Relevance"
        value={relevance}
        drivers={breakdown?.relevance}
        render={(d) => `engagement ${d.engagement ?? 0}`}
        fallback={`Commercial fit inferred from ${fallbackList}`}
      />
    </div>
  );
}
